/**
 * Registered Element Attribute Mappings
 * 
 * This file contains the default registrations for element attribute mappings,
 * defining which attributes of each element type can be bound to dynamic content.
 */

import { ElementAttributeRegistry, ElementAttributeMapping } from './elementAttributeMapping';

/**
 * Text Element Attribute Mappings
 */
const textElementMapping: ElementAttributeMapping = {
  elementType: 'text',
  displayName: 'Text Element',
  attributes: [
    {
      attributeKey: 'content',
      attributeLabel: 'Text Content',
      compatibleMediaTypes: ['text', 'date', 'int', 'float'],
      defaultValue: { mode: 'static', mediaType: 'text', staticValue: 'Sample Text' },
      required: true,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The text content displayed in the element',
        category: 'content',
        priority: 1
      }
    },
    {
      attributeKey: 'color',
      attributeLabel: 'Text Color',
      compatibleMediaTypes: ['color'],
      defaultValue: { mode: 'static', mediaType: 'color', staticValue: '#000000' },
      required: false,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The color of the text',
        category: 'appearance',
        priority: 2
      }
    }
  ]
};

/**
 * Rectangle Element Attribute Mappings
 */
const rectElementMapping: ElementAttributeMapping = {
  elementType: 'rect',
  displayName: 'Rectangle Element',
  attributes: [
    {
      attributeKey: 'fill.color',
      attributeLabel: 'Fill Color',
      compatibleMediaTypes: ['color'],
      defaultValue: { mode: 'static', mediaType: 'color', staticValue: '#ffffff' },
      required: false,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The fill color of the rectangle',
        category: 'appearance',
        priority: 1
      }
    },
    {
      attributeKey: 'stroke.color',
      attributeLabel: 'Stroke Color',
      compatibleMediaTypes: ['color'],
      defaultValue: { mode: 'static', mediaType: 'color', staticValue: '#000000' },
      required: false,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The stroke color of the rectangle',
        category: 'appearance',
        priority: 2
      }
    }
  ]
};

/**
 * Circle Element Attribute Mappings
 */
const circleElementMapping: ElementAttributeMapping = {
  elementType: 'circle',
  displayName: 'Circle Element',
  attributes: [
    {
      attributeKey: 'fill.color',
      attributeLabel: 'Fill Color',
      compatibleMediaTypes: ['color'],
      defaultValue: { mode: 'static', mediaType: 'color', staticValue: '#ffffff' },
      required: false,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The fill color of the circle',
        category: 'appearance',
        priority: 1
      }
    },
    {
      attributeKey: 'stroke.color',
      attributeLabel: 'Stroke Color',
      compatibleMediaTypes: ['color'],
      defaultValue: { mode: 'static', mediaType: 'color', staticValue: '#000000' },
      required: false,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The stroke color of the circle',
        category: 'appearance',
        priority: 2
      }
    }
  ]
};

/**
 * Image Element Attribute Mappings
 */
const imageElementMapping: ElementAttributeMapping = {
  elementType: 'image',
  displayName: 'Image Element',
  attributes: [
    {
      attributeKey: 'source',
      attributeLabel: 'Image Source',
      compatibleMediaTypes: ['image'],
      defaultValue: { mode: 'static', mediaType: 'image', staticValue: { path: 'defaults/image_placeholder.png' } },
      required: true,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The source URL or path of the image',
        category: 'content',
        priority: 1
      }
    }
  ]
};

/**
 * SVG Element Attribute Mappings
 */
const svgElementMapping: ElementAttributeMapping = {
  elementType: 'svg',
  displayName: 'SVG Element',
  attributes: [
    {
      attributeKey: 'source',
      attributeLabel: 'SVG Source',
      compatibleMediaTypes: ['image'],
      defaultValue: { mode: 'static', mediaType: 'image', staticValue: { path: 'defaults/image_placeholder.png' } },
      required: true,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'The source URL or path of the SVG',
        category: 'content',
        priority: 1
      }
    },
    {
      attributeKey: 'monochromeColor',
      attributeLabel: 'Monochrome Color',
      compatibleMediaTypes: ['color'],
      defaultValue: { mode: 'static', mediaType: 'color', staticValue: '#000000' },
      required: false,
      editorComponent: 'StaticDynamicSelector',
      generatorComponent: 'EntityValueSelector',
      meta: {
        description: 'Color overlay for monochrome mode',
        category: 'appearance',
        priority: 2
      }
    }
  ]
};

/**
 * Register all element attribute mappings
 */
export function registerElementAttributes(registry: ElementAttributeRegistry): void {
  registry.register(textElementMapping);
  registry.register(rectElementMapping);
  registry.register(circleElementMapping);
  registry.register(imageElementMapping);
  registry.register(svgElementMapping);
}